import { com } from '@eidu/form'
import { logMessage } from '../../../util/Logging'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import entityTypePaginatedResponseFromJsonString = com.eidu.sharedlib.entity.api.types.entityTypePaginatedResponseFromJsonString
import PostTypesResponse = com.eidu.sharedlib.entity.api.types.PostTypesResponse
import IndexedPaginatedResponse = com.eidu.api.pagination.IndexedPaginatedResponse

const GENERIC_KIND_NAME = EntityTypeKind.Generic.name
const supportedEntityTypeKinds = new Set<string>(EntityTypeKind.values().map((kind) => kind.name))
const warnedUnsupportedEntityTypeKinds = new Set<string>()

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isEntityTypeLikeRecord = (value: Record<string, unknown>): value is Record<string, unknown> & { kind: string } =>
  typeof value.kind === 'string' &&
  typeof value.name === 'string' &&
  Array.isArray(value.fields) &&
  Object.hasOwn(value, 'label')

const normalizeEntityTypeKind = (kindName: string) => {
  if (supportedEntityTypeKinds.has(kindName)) return kindName

  if (!warnedUnsupportedEntityTypeKinds.has(kindName)) {
    warnedUnsupportedEntityTypeKinds.add(kindName)
    logMessage(
      `Unsupported entity type kind "${kindName}" received from API. Falling back to "${GENERIC_KIND_NAME}".`,
      'warning',
      'EntityTypeCompatibility'
    )
  }

  return GENERIC_KIND_NAME
}

const normalizeEntityTypeKindsInValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeEntityTypeKindsInValue(entry))
  }

  if (!isRecord(value)) return value

  const normalizedRecord: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, entryValue]) => {
    normalizedRecord[key] = normalizeEntityTypeKindsInValue(entryValue)
  })

  if (isEntityTypeLikeRecord(normalizedRecord)) {
    normalizedRecord.kind = normalizeEntityTypeKind(normalizedRecord.kind)
  }

  return normalizedRecord
}

const normalizeEntityTypeResponseJson = (jsonString: string) => {
  try {
    return JSON.stringify(normalizeEntityTypeKindsInValue(JSON.parse(jsonString)))
  } catch {
    return jsonString
  }
}

export const parseEntityTypeFromJsonString = (jsonString: string): EntityType =>
  EntityType.fromJsonString(normalizeEntityTypeResponseJson(jsonString))

export const parseEntityTypePaginatedResponseFromJsonString = (
  jsonString: string
): IndexedPaginatedResponse<EntityType> =>
  entityTypePaginatedResponseFromJsonString(
    normalizeEntityTypeResponseJson(jsonString)
  ) as IndexedPaginatedResponse<EntityType>

export const parsePostTypesResponseFromJsonString = (jsonString: string): PostTypesResponse =>
  PostTypesResponse.fromJsonString(normalizeEntityTypeResponseJson(jsonString))
